import { SignupEmailPreview } from "./SignupEmailPreview";
import type { CollectionTemplateBundleDto } from "@/api/collectionBundles";
import type { MaterialAudience } from "@/api/portalMaterials";

// WS2 — Step 4 "Review & send" of the Send Signup Link wizard.
// Summary of every entry across steps 1-3 plus a preview of the email
// the recipient will receive.

export interface Step4ReviewAssignee {
  id: string;
  displayName: string;
  isSender: boolean;
}

export interface Step4ReviewProps {
  email: string;
  fullName: string;
  accountType: MaterialAudience | "";
  selectedBundles: CollectionTemplateBundleDto[];
  assignees: Step4ReviewAssignee[];
  senderName: string;
}

function SummaryRow({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <div className="flex flex-col gap-space-1 sm:flex-row sm:items-baseline sm:gap-space-3">
      <span className="text-caption text-gray-mid font-display uppercase tracking-wider sm:w-40 shrink-0">
        {label}
      </span>
      <span className="text-small text-onyx min-w-0">{children}</span>
    </div>
  );
}

export function Step4Review({
  email,
  fullName,
  accountType,
  selectedBundles,
  assignees,
  senderName,
}: Step4ReviewProps) {
  return (
    <div className="flex flex-col gap-space-4">
      <p className="text-small text-gray-mid m-0 max-w-reading">
        Review the details below, then send the signup link. The recipient
        will get the email previewed on the right.
      </p>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-space-4">
        <section
          className="flex flex-col gap-space-3 bg-white-pure rounded-lg border border-border-light p-space-4"
          data-testid="signup-review-summary"
        >
          <h3 className="font-display font-bold text-small uppercase tracking-wider text-gold-shadow m-0">
            Summary
          </h3>
          <div className="flex flex-col gap-space-3">
            <SummaryRow label="Email">
              <span className="break-all">{email || "—"}</span>
            </SummaryRow>
            <SummaryRow label="Full name">{fullName || "—"}</SummaryRow>
            <SummaryRow label="Party type">{accountType || "—"}</SummaryRow>
            <SummaryRow label="Bundles">
              {selectedBundles.length === 0 ? (
                <span className="text-gray-mid">
                  None — bundles can be assigned later from the account page.
                </span>
              ) : (
                <ul className="list-disc pl-space-4 m-0">
                  {selectedBundles.map((b) => (
                    <li key={b.id}>{b.name}</li>
                  ))}
                </ul>
              )}
            </SummaryRow>
            <SummaryRow label="Assigned staff">
              {assignees.length === 0 ? (
                <span className="text-gray-mid">—</span>
              ) : (
                <ul className="list-none p-0 m-0 flex flex-col gap-space-1">
                  {assignees.map((a) => (
                    <li key={a.id}>
                      {a.displayName}
                      {a.isSender && (
                        <span className="ml-space-1 text-caption text-gold-shadow uppercase tracking-wide">
                          You
                        </span>
                      )}
                    </li>
                  ))}
                </ul>
              )}
            </SummaryRow>
          </div>
        </section>

        <section className="flex flex-col gap-space-2">
          <h3 className="font-display font-bold text-small uppercase tracking-wider text-gold-shadow m-0">
            Email preview
          </h3>
          <SignupEmailPreview
            fullName={fullName}
            senderName={senderName}
            partyType={accountType}
            bundleNames={selectedBundles.map((b) => b.name)}
          />
        </section>
      </div>
    </div>
  );
}
